import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CardContent, Grid, MenuItem, TextField } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { IconUserPlus, IconDeviceFloppy } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const emptyForm = {
    full_name: '',
    cpf: '',
    birth_date: '',
    email: '',
    phone: '',
    address: '',
    role: '',
    department: '',
    branch: '',
    admission_date: '',
    termination_date: '',
    salary: '',
    status: 'active',
    bank_account: ''
};

const ColaboradorForm = () => {
    const history = useHistory();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [appId, setAppId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [branches, setBranches] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const mountedRef = useRef(true);

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadOptions();
        if (isEdit) loadColaborador();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadOptions = async () => {
        try {
            const [rolesRes, departmentsRes, companyRes] = await Promise.all([
                api.get(`v1/apps/${appId}/roles/`),
                api.get(`v1/apps/${appId}/departments/`),
                api.get('v1/apps/default/')
            ]);
            setRoles(rolesRes.data.roles || []);
            setDepartments(departmentsRes.data.departments || []);

            const companyId = companyRes.data.default_company_id;
            const [branchesRes, bankAccountsRes] = await Promise.all([
                api.get(`v1/apps/${appId}/branches/?company=${companyId}`),
                api.get(`v1/apps/${appId}/bank-accounts/?company=${companyId}`)
            ]);
            setBranches(branchesRes.data.branches || branchesRes.data || []);
            setBankAccounts(bankAccountsRes.data.bank_accounts || []);
        } catch (error) {
            console.error(error);
        }
    };

    const loadColaborador = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/colaboradores/${id}/`);
            setForm({
                full_name: res.data.full_name || '',
                cpf: res.data.cpf || '',
                birth_date: res.data.birth_date || '',
                email: res.data.email || '',
                phone: res.data.phone || '',
                address: res.data.address || '',
                role: res.data.role || '',
                department: res.data.department || '',
                branch: res.data.branch || '',
                admission_date: res.data.admission_date || '',
                termination_date: res.data.termination_date || '',
                salary: res.data.salary || '',
                status: res.data.status || 'active',
                bank_account: res.data.bank_account || ''
            });
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar colaborador' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!form.full_name) {
            setStatus({ type: 'error', message: 'Informe o nome completo' });
            return;
        }
        if (!form.admission_date) {
            setStatus({ type: 'error', message: 'Informe a data de admissão' });
            return;
        }
        if (!form.salary || Number(form.salary) <= 0) {
            setStatus({ type: 'error', message: 'Informe um salário maior que zero' });
            return;
        }
        if (form.status === 'terminated' && !form.termination_date) {
            setStatus({ type: 'error', message: 'Informe a data de desligamento' });
            return;
        }

        setSaving(true);
        try {
            const payload = { ...form };
            if (form.status !== 'terminated') payload.termination_date = '';

            if (isEdit) {
                await api.patch(`v1/apps/${appId}/colaboradores/${id}/`, payload);
            } else {
                await api.post(`v1/apps/${appId}/colaboradores/`, payload);
            }
            history.push('/dashboard/hr/colaboradores');
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar colaborador' });
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    if (loading) return null;

    return (
        <Box>
            <PageHeader
                icon={IconUserPlus}
                gradient="blue"
                title={isEdit ? 'Editar Colaborador' : 'Novo Colaborador'}
                subtitle="Cadastro de funcionário"
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Nome completo"
                                fullWidth
                                required
                                value={form.full_name}
                                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField label="CPF" fullWidth value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Data de nascimento"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={form.birth_date}
                                onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField label="E-mail" fullWidth value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField label="Telefone" fullWidth value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Endereço" fullWidth value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                label="Cargo"
                                fullWidth
                                value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                            >
                                <MenuItem value="">Nenhum</MenuItem>
                                {roles.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        {role.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                label="Departamento"
                                fullWidth
                                value={form.department}
                                onChange={(e) => setForm({ ...form, department: e.target.value })}
                            >
                                <MenuItem value="">Nenhum</MenuItem>
                                {departments.map((department) => (
                                    <MenuItem key={department.id} value={department.id}>
                                        {department.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                label="Filial"
                                fullWidth
                                value={form.branch}
                                onChange={(e) => setForm({ ...form, branch: e.target.value })}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {branches.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Data de admissão"
                                type="date"
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                                value={form.admission_date}
                                onChange={(e) => setForm({ ...form, admission_date: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Salário"
                                type="number"
                                fullWidth
                                required
                                value={form.salary}
                                onChange={(e) => setForm({ ...form, salary: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                select
                                label="Status"
                                fullWidth
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                            >
                                <MenuItem value="active">Ativo</MenuItem>
                                <MenuItem value="on_leave">Afastado</MenuItem>
                                <MenuItem value="terminated">Desligado</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Data de desligamento"
                                type="date"
                                fullWidth
                                disabled={form.status !== 'terminated'}
                                InputLabelProps={{ shrink: true }}
                                value={form.termination_date}
                                onChange={(e) => setForm({ ...form, termination_date: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                label="Conta bancária"
                                fullWidth
                                value={form.bank_account}
                                onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {bankAccounts.map((bankAccount) => (
                                    <MenuItem key={bankAccount.id} value={bankAccount.id}>
                                        {bankAccount.bank_name} - Ag {bankAccount.agency_number} - CC {bankAccount.account_number}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                startIcon={<IconDeviceFloppy size={18} />}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

export default ColaboradorForm;
